import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, List, ListItem, ListItemAvatar, ListItemText, Avatar, TextField, IconButton, CircularProgress, Autocomplete, Badge } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ForumIcon from '@mui/icons-material/Forum';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import api from '../../api';

const ChatSidebar = ({ channels, activeChat, setActiveChat, loadingChannels, onStartDM, onOpenCreateModal, safeEmpId, baseUrl }) => {
  const [searchOptions, setSearchOptions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const safeChannelsList = Array.isArray(channels) ? channels : [];
  
  const directChats = safeChannelsList.filter(c => c.type === 'DIRECT');
  const groupChats = safeChannelsList.filter(c => c.type !== 'DIRECT');

  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchOptions([]);
      return;
    }
    const delay = setTimeout(() => {
      setIsSearching(true);
      api.get(`/chat/search-employees?q=${searchQuery}&current_emp_id=${safeEmpId}`)
        .then(res => setSearchOptions(Array.isArray(res.data) ? res.data : []))
        .catch(err => console.error(err))
        .finally(() => setIsSearching(false));
    }, 400);
    return () => clearTimeout(delay);
  }, [searchQuery, safeEmpId]);

  const renderChatItem = (chat, isGroup) => {
    const isSelected = activeChat?.id === chat.id;
    const profilePicUrl = chat.target_emp_id ? `${baseUrl}/employees/${chat.target_emp_id}/profile-picture?t=${Date.now()}` : null;
    const channelPicUrl = chat.image_url ? chat.image_url : null;
    const unreadCount = parseInt(chat.unread_count || 0);

    return (
      <ListItem 
        key={chat.id}
        button 
        selected={isSelected}
        onClick={() => setActiveChat(chat)}
        sx={{ 
          py: 0.4,  // Reduced vertical padding
          px: 1.5,
          borderRadius: 1.5,
          mx: 0.8,
          minHeight: 38,  // Reduced height
          width: 'auto',
          '&.Mui-selected': { 
            bgcolor: 'rgba(91, 95, 199, 0.1)', 
            color: '#5b5fc7', 
            '&:hover': { bgcolor: 'rgba(91, 95, 199, 0.15)' } 
          },
          '&:hover': { bgcolor: '#f1f5f9' },
        }}
      >
        <ListItemAvatar sx={{ minWidth: 36 }}>  {/* Reduced from 42 */}
          <Badge 
            color="error" 
            badgeContent={unreadCount} 
            invisible={unreadCount === 0}
            sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16 } }}
          >
            {isGroup ? (
              <Avatar src={channelPicUrl} sx={{ width: 28, height: 28, bgcolor: isSelected ? '#5b5fc7' : '#e0e0e0', color: isSelected ? '#fff' : '#555', fontSize: '0.75rem', fontWeight: 'bold' }}>
                {chat.name ? chat.name.charAt(0).toUpperCase() : '#'}
              </Avatar>
            ) : (
              <Avatar src={profilePicUrl} sx={{ width: 28, height: 28, bgcolor: '#1976d2', fontSize: '0.75rem' }}>
                {chat.name ? chat.name.charAt(0).toUpperCase() : '?'}
              </Avatar>
            )}
          </Badge>
        </ListItemAvatar>
        <ListItemText 
          primary={
            <Typography variant="body2" sx={{ 
              fontWeight: isSelected || unreadCount > 0 ? 700 : 500, 
              color: isSelected ? '#5b5fc7' : '#202124',
              fontSize: '0.8rem'  // Smaller font
            }}>
              {chat.name || 'Unnamed'}
            </Typography>
          } 
        />
      </ListItem>
    );
  };

  return (
    <Paper elevation={0} sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      borderRadius: 0, 
      overflow: 'hidden', 
      bgcolor: '#ffffff',
      height: '100%',
      width: '100%',
      border: 'none'
    }}>
      {/* Header - Reduced padding */}
      <Box sx={{ 
        p: 1.5, 
        pb: 1,
        borderBottom: '1px solid #e0e0e0',
        flexShrink: 0
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ 
            fontWeight: 700, 
            color: '#10394f', 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontSize: '0.95rem'
          }}>
            <ForumIcon color="primary" sx={{ fontSize: 20 }} /> Connect+
          </Typography>
          <IconButton 
            size="small" 
            sx={{ color: '#5b5fc7', padding: 0.5 }} 
            onClick={onOpenCreateModal} 
            title="Create Group Channel"
          >
            <GroupAddIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
        
        <Autocomplete
          options={searchOptions}
          getOptionLabel={(option) => `${option.full_name} (${option.emp_id})`}
          onInputChange={(event, newInputValue) => setSearchQuery(newInputValue)}
          onChange={(e, val) => {
            onStartDM(val);
            setSearchQuery('');
          }}
          loading={isSearching}
          filterOptions={(x) => x}
          clearOnBlur
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Start a new chat..."
              sx={{ 
                mt: 1, 
                bgcolor: '#f5f5f5', 
                borderRadius: 1, 
                '& fieldset': { border: 'none' },
                '& .MuiInputBase-root': { height: 34 }  // Reduced height
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: <SearchIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.5, ml: 1 }} />,
                endAdornment: (
                  <React.Fragment>
                    {isSearching ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      </Box>
      
      {/* Chat List - No extra padding */}
      <List sx={{ 
        flexGrow: 1, 
        overflowY: 'auto', 
        p: 0.5, 
        pt: 0.5,
        '&::-webkit-scrollbar': {
          width: 4,
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: 2,
        },
      }}>
        {loadingChannels ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : safeChannelsList.length === 0 ? (
          <Typography variant="body2" sx={{ p: 2, textAlign: 'center', color: 'text.secondary', fontSize: '0.8rem' }}>
            No chats yet.
          </Typography>
        ) : (
          <>
            {/* Group Channels Section */}
            {groupChats.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ 
                  px: 1.5, 
                  py: 0.3, 
                  display: 'block',
                  color: 'text.secondary', 
                  fontWeight: 600, 
                  letterSpacing: 0.5,
                  fontSize: '0.6rem',
                  textTransform: 'uppercase'
                }}>
                  Channels
                </Typography>
                {groupChats.map(chat => renderChatItem(chat, true))}
              </Box>
            )}
            
            {/* Direct Messages Section */}
            {directChats.length > 0 && (
              <Box sx={{ mt: groupChats.length > 0 ? 0.5 : 0 }}>
                <Typography variant="caption" sx={{ 
                  px: 1.5, 
                  py: 0.3, 
                  display: 'block',
                  color: 'text.secondary', 
                  fontWeight: 600, 
                  letterSpacing: 0.5,
                  fontSize: '0.6rem',
                  textTransform: 'uppercase'
                }}>
                  Direct Messages
                </Typography>
                {directChats.map(chat => renderChatItem(chat, false))}
              </Box>
            )}
          </>
        )}
      </List>
    </Paper>
  );
};

export default ChatSidebar;